import { Text, Icon, Button } from "@virkesborsen/ui-component-library";
import { useEffect, useRef, useState, useCallback } from "react";
import ComparisonSlider from "../../../../commons/ComparisonSliderTW";
import { useDrone } from "../../../context/DroneContext";

const dronePerkItems = [
  {
    title: "Upptäck skador i tid",
    description:
      "Skydda ditt virkesvärde från granbarkborre och väderskador genom att upptäcka och agera tidigt.",
    image: "img/drone/discover_damage_early.png",
  },
  {
    title: "Se skogen som den är",
    description:
      "Uppdatera din skogsbruksplan med hög precision, var du än befinner dig. ",
    image: "img/drone/see_forest_as_is.png",
  },
  {
    title: "Följ upp åtgärder",
    description:
      "Få en tydlig bild av din skog efter avverkning, gallring eller återplantering, utan att behöva besöka varje del av fastigheten.",
    image: "img/drone/follow_up_actions.png",
  },
  {
    title: "Planera smartare",
    description:
      "Spara tid och pengar genom att agera där det verkligen behövs.",
    image: "img/drone/plan_smarter.png",
  },
];

export default function DroneInfo() {
  const { openCheckout } = useDrone();
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const videoSrc =
    window.innerWidth < 780
      ? "copture_video_360.mp4"
      : "copture_video_1080p.mp4";

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIOSDevice);
  }, []);

  // Ensures the video plays even when browsers have strict autoplay policies
  useEffect(() => {
    if (videoRef.current) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.error("Autoplay was prevented:", error);
          setVideoError(true);
        }
      };
      playVideo();
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoEnded = () => {
      video.currentTime = 0;
      video.play().catch((err) => console.error("Loop play error:", err));
    };

    video.addEventListener("ended", handleVideoEnded);

    return () => {
      video.removeEventListener("ended", handleVideoEnded);
    };
  }, [isIOS]);

  return (
    <div className="h-full w-full bg-stone-50">
      <div className="max-w-[1320px] mx-auto flex flex-col">
        <section className="flex gap-8 p-4 flex-col-reverse lg:flex-row lg:p-16">
          <div className="flex flex-col flex-1 lg:justify-center">
            <Text variant="displayAndHeadlineMedium" className="pb-4">
              Drönaranalys. Direkt i din skogsbruksplan
            </Text>
            <Text
              variant="introLeadSmallRegular"
              className="text-pebble-500 pb-12"
            >
              Få en exakt överblick över din skog med högupplösta drönarbilder –
              direkt i din digitala skogsbruksplan på Treebula. Se skogen precis
              som den är, upptäck skador i tid och planera åtgärder smartare.
              Ortofoto och skadeanalys hjälper dig fatta bättre beslut, spara
              tid och skydda dina värden.
            </Text>
            <div className="w-full bg-white-50 border border-white-600 border-solid rounded-xl p-4 flex flex-col gap-4">
              <div className="flex flex-row gap-2 justify-start items-start">
                <Icon
                  tw
                  name="DroneSimple"
                  size="large"
                  spacing="medium"
                  className="fill-brown-700 bg-stone-200"
                />
                <div className="flex flex-col flex-1 h-full justify-center">
                  <div className="flex flex-col gap-1">
                    <Text variant="labelMediumMediumDense">Drönaranalys</Text>

                    <Text
                      variant="labelMediumRegularDense"
                      className="text-cloud-900 mb-3"
                    >
                      Högupplösta drönarbilder i din skogsbruksplan 
                    </Text>
                  </div>
                </div>
              </div>
              <Button tw variant="primary" fullWidth onClick={openCheckout}>
                Beställ drönarflygning
              </Button>
            </div>
          </div>
          <div className="flex-1 rounded-xl overflow-hidden w-full">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onError={() => setVideoError(true)}
              className="w-full h-full object-cover lg:h-[600px]"
            >
              {!videoError && (
                <source
                  src={`${Window.static_path}video/${videoSrc}`}
                  type="video/mp4"
                />
              )}
            </video>
          </div>
        </section>

        <section className="flex gap-8 p-4 flex-col-reverse lg:flex-row lg:p-16">
          <div className="flex-1 rounded-xl overflow-hidden">
            <ComparisonSlider
              leftImage={`${Window.static_path}img/drone/satellite-photo.png`}
              rightImage={`${Window.static_path}img/drone/drone-photo.png`}
              leftImageText="Satelit"
              rightImageText="Ortofoto"
            />
          </div>
          <div className="flex-1 flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <Text variant="overlineLargeRegular">Upptäck skador tidigt</Text>
              <Text variant="sectionHeaderMedium">Vad är en drönaranalys?</Text>
            </div>
            <div className="flex flex-col gap-8">
              <Text variant="introLeadSmallRegular" className="text-pebble-500">
                En drönaranalys ger dig en uppdaterad, detaljerad bild av din
                skog – sedd från ovan. Med hjälp av ortofoton, alltså
                högupplösta bilder tagna med drönare, kan du se exakt hur din
                skog ser ut just nu.
              </Text>
              <Text variant="introLeadSmallRegular" className="text-pebble-500">
                Till skillnad från vanliga satellitbilder visar ortofoton träd,
                hyggen, vägar och skador med stor skärpa. Du får en visuell
                karta över hela din fastighet – perfekt för planering,
                uppföljning och insyn i skogens tillstånd.
              </Text>
              <Text
                variant="introLeadSmallRegular"
                className="text-pebble-500 pb-12"
              >
                Vill du gå ett steg längre kan du även lägga till skadeanalys.
                Då analyseras bilderna och visar var det finns angrepp av
                granbarkborre eller skador som toppbrott och snöskador. Allt
                levereras direkt i din digitala skogsbruksplan – så att du har
                koll på läget var du än befinner dig.
              </Text>
            </div>
            <div>
              <Button
                tw
                variant="primary"
                className="shrink"
                onClick={openCheckout}
              >
                Beställ drönarflygning
              </Button>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-12 p-4 lg:p-16">
          <div className="flex flex-col gap-4">
            <Text variant="overlineLargeRegular">Övervaka din skog</Text>
            <Text variant="sectionHeaderMedium">
              Vad får du med en drönaranalys
            </Text>

            <Text variant="introLeadSmallRegular" className="text-pebble-500">
              Du får en omfattande bild av din skogsmark. Vi använder
              banbrytande teknik för att övervaka trädens hälsa och identifiera
              eventuella skadedjur. Tjänsten ger dig värdefulla insikter om
              markens tillstånd, vilket underlättar förvaltningen av dina
              skogsresurser.
            </Text>
          </div>
          <div className="flex flex-col gap-8 lg:flex-row">
            {dronePerkItems.map((item, index) => (
              <div className="flex flex-col flex-1">
                <div className="overflow-hidden rounded-xl mb-6">
                  <img
                    src={`${Window.static_path}${item.image}`}
                    alt="Skogsövervakning exempel"
                    className="w-full h-full object-cover max-h-[333px]"
                  />
                </div>
                <Text variant="subheaderSmallMedium" className="mb-4">
                  {item.title}
                </Text>
                <Text
                  variant="bodyMediumBaseRegular"
                  className="text-pebble-500"
                >
                  {item.description}
                </Text>
              </div>
            ))}
          </div>
          <div>
            <Button
              tw
              variant="primary"
              className="shrink"
              onClick={openCheckout}
            >
              Beställ drönarflygning
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
